import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useArticle } from "api/use-intercom";
import LoadingSpinner from "components/loading-spinner";
// import { useTranslation } from "react-i18next";
import "./article.css";
import ScrollToTop from "components/scroll-to-top";
import DOMPurify from "dompurify";

const TableOfContents = ({ sections }) => {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 150;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="hidden lg:block w-80 xl:w-40">
      <div className="p-4 fixed right-8 top-[11rem] w-64">
        <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
        <ul className="space-y-2">
          {sections.map((section) => (
            <li
              key={section.id}
              className={`pl-4 border-l-2 cursor-pointer hover:text-blue-600 transition-colors ${
                activeSection === section.id
                  ? "border-black text-black"
                  : "border-gray-200 text-gray-600"
              }`}
              onClick={() => scrollToSection(section.id)}
            >
              {section.title}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default function ArticlePage() {
  const { id: rawId } = useParams();
  const id = rawId.split("-")[0];
  const { data: article, isLoading, error } = useArticle(id);

  const extractSections = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const h1Elements = doc.getElementsByTagName("h1");
    return Array.from(h1Elements).map((h1) => ({
      id: h1.id,
      title: h1.textContent.replace(/^\s*Step \d+:\s*/, "").trim(),
    }));
  };

  // Helper to process article HTML
  const processArticleHtml = (html) => {
    // Replace the domain
    let processed = html.replaceAll(
      /intercom\.help\/legobricktakeback\/en\//g,
      "legobricktakeback.com/en-US/help/"
    );
    // Parse and update <a> tags
    const parser = new DOMParser();
    const doc = parser.parseFromString(processed, "text/html");
    doc.querySelectorAll("a").forEach((a) => {
      // Remove target attribute to open in same tab
      a.removeAttribute("target");
    });
    // Serialize back to string
    return doc.body.innerHTML;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <ScrollToTop />
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Error loading article
            </h1>
            <p className="text-gray-600">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Sorry, we couldn't find this article.
            </h1>
            <p className="text-gray-600">
              The article you're looking for might have been moved or deleted.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const sections = extractSections(article.body);

  return (
    <div className="min-h-screen bg-gray-300/25 flex justify-center">
      {/* <div className="fixed left-4 top-[11.5rem] text-lg mr-8">
        <span className="material-symbols-outlined scale-75 translate-y-[0.3rem]">
          arrow_back
        </span>{" "}
        Back
      </div> */}
      <ScrollToTop />
      <div className="md:px-8 my-0 md:my-12 py-8 md:py-0 relative bg-white rounded-xl">
        <div className="md:mt-10 px-[1rem] flex items-center text-sm text-tertiary flex-wrap gap-y-2 md:gap-y-0">
          <Link
            to="/en-US/help"
            className="hover:text-gray-600 whitespace-nowrap"
          >
            Help Center
          </Link>
          <span className="material-symbols-outlined scale-75 whitespace-nowrap">
            chevron_forward
          </span>
          {article.collection && (
            <>
              <Link
                to={`/en-US/help/collections/${article.collection.id}`}
                className="hover:text-gray-600 whitespace-nowrap"
              >
                {article.collection.name}
              </Link>
              <span className="material-symbols-outlined scale-75 whitespace-nowrap">
                chevron_forward
              </span>
            </>
          )}
          <span className="text-black whitespace-nowrap">{article.title}</span>
        </div>
        <article className="article-container">
          <h1 className="article-title">{article.title}</h1>
          {article.description && (
            <p className="article-description">{article.description}</p>
          )}
          <div
            className="article-content"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(processArticleHtml(article.body)),
            }}
          />
        </article>
      </div>
      {sections.length > 0 && <TableOfContents sections={sections} />}
    </div>
  );
}
